import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { taskService } from '../services/taskService';
import { Task, TaskFormData } from '../types';
import './Tasks.css';

type FilterType = 'all' | 'active' | 'completed' | 'high';

const Tasks: React.FC = () => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: ''
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async (): Promise<void> => {
    try {
      const tasks = await taskService.getTasks();
      setTasks(tasks);
      setLoading(false);
    } catch (error) {
      console.error('Görevler yüklenemedi:', error);
      setLoading(false);
    }
  };

  const filterTasks = useCallback((): void => {
    let filtered = [...tasks];
    
    switch (filter) {
      case 'active':
        filtered = tasks.filter(t => !t.completed);
        break;
      case 'completed':
        filtered = tasks.filter(t => t.completed);
        break;
      case 'high':
        filtered = tasks.filter(t => t.priority === 'high');
        break;
      default:
        filtered = tasks;
    }
    
    setFilteredTasks(filtered);
  }, [tasks, filter]);

  useEffect(() => {
    filterTasks();
  }, [filterTasks]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    try {
      if (editingTask) {
        await taskService.updateTask(editingTask._id, formData);
      } else {
        await taskService.createTask(formData);
      }
      
      fetchTasks();
      closeModal();
    } catch (error) {
      console.error('Görev kaydedilemedi:', error);
    }
  };

  const handleDelete = async (id: string): Promise<void> => {
    if (window.confirm(t('tasks.deleteConfirm'))) {
      try {
        await taskService.deleteTask(id);
        fetchTasks();
      } catch (error) {
        console.error('Görev silinemedi:', error);
      }
    }
  };

  const handleToggleComplete = async (id: string, completed: boolean): Promise<void> => {
    try {
      await taskService.toggleTaskComplete(id, completed);
      fetchTasks();
    } catch (error) {
      console.error('Görev durumu değiştirilemedi:', error);
    }
  };

  const openModal = (task: Task | null = null): void => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description || '',
        priority: task.priority,
        dueDate: task.dueDate ? task.dueDate.split('T')[0] : ''
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = (): void => {
    setShowModal(false);
    setEditingTask(null);
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="tasks-page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('tasks.title')}</h1>
          <p className="page-subtitle">{t('tasks.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <FiPlus /> {t('tasks.addTask')}
        </button>
      </div>

      <div className="filter-bar">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          {t('tasks.filters.all')} ({tasks.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
          onClick={() => setFilter('active')}
        >
          {t('tasks.filters.active')} ({tasks.filter(t => !t.completed).length})
        </button>
        <button 
          className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter('completed')}
        >
          {t('tasks.filters.completed')} ({tasks.filter(t => t.completed).length})
        </button>
        <button 
          className={`filter-btn ${filter === 'high' ? 'active' : ''}`}
          onClick={() => setFilter('high')}
        >
          {t('tasks.filters.highPriority')} ({tasks.filter(t => t.priority === 'high').length})
        </button>
      </div>

      {filteredTasks.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📝</div>
          <h3 className="empty-state-title">{t('tasks.noTasks')}</h3>
          <p className="empty-state-text">{t('tasks.createFirst')}</p>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <FiPlus /> {t('tasks.addTask')}
          </button>
        </div>
      ) : (
        <div className="tasks-grid">
          {filteredTasks.map(task => (
            <div key={task._id} className={`task-card ${task.completed ? 'completed' : ''}`}>
              <div className="task-card-header">
                <input
                  type="checkbox"
                  checked={task.completed}
                  onChange={() => handleToggleComplete(task._id, !task.completed)}
                  className="task-checkbox"
                />
                <span className={`task-priority-badge priority-${task.priority}`}>
                  {t(`tasks.priority.${task.priority}`)}
                </span>
              </div>
              
              <h3 className="task-card-title">{task.title}</h3>
              {task.description && (
                <p className="task-card-description">{task.description}</p>
              )}
              
              {task.dueDate && (
                <p className="task-card-date">
                  📅 {new Date(task.dueDate).toLocaleDateString('tr-TR')}
                </p>
              )}
              
              <div className="task-card-actions">
                <button 
                  className="btn-icon btn-edit" 
                  onClick={() => openModal(task)}
                  title={t('common.edit')}
                >
                  <FiEdit2 />
                </button>
                <button 
                  className="btn-icon btn-delete" 
                  onClick={() => handleDelete(task._id)}
                  title={t('common.delete')}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTask ? t('tasks.editTask') : t('tasks.newTask')}</h2>
              <button className="modal-close" onClick={closeModal}>
                <FiX />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="input-group">
                <label className="input-label">Başlık *</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">Açıklama</label>
                <textarea
                  className="textarea-field"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="input-group">
                  <label className="input-label">Öncelik</label>
                  <select
                    className="select-field"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value as 'low' | 'medium' | 'high'})}
                  >
                    <option value="low">Düşük</option>
                    <option value="medium">Orta</option>
                    <option value="high">Yüksek</option>
                  </select>
                </div>

                <div className="input-group">
                  <label className="input-label">Bitiş Tarihi</label>
                  <input
                    type="date"
                    className="input-field"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiCheck /> {editingTask ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
